/* eslint-disable react-refresh/only-export-components */ // FIXME
import { Col, Flex, List, Row } from "antd"
import { DataSourceChoice } from "src/pages/DataAssets/drawers/NewAssetDrawer/NewAssetDrawer.tsx"
import { useState } from "react"
import { useQuery } from "@apollo/client"

// eslint-disable-next-line no-restricted-imports -- deprecated file
import {
  DatasourceWithRunsFragment,
  DatasourcesWithRunsDocument,
  DatasourcesWithRunsQuery,
} from "src/api/graphql/graphql-operations.ts"
import { StyledLogoContainer } from "src/global/ui/List/MainContentList.tsx"
import { getImageLogo } from "src/pages/DataAssets/getImageLogo.ts"
import { Maybe } from "graphql/jsutils/Maybe"
import { Segmented } from "src/global/ui/Segmented/Segmented.tsx"
import {
  NativeCloudSupportedDataSource,
  isSupportedDataSource,
} from "src/pages/DataAssets/schemas/data-source-schemas.ts"
import { useIsDataSourceEditable } from "src/global/hooks/useIsDataSourceEditable.ts"
import { DataSourceCards } from "src/pages/DataAssets/components/DataSourceCards"
import { BodyStrong, DescriptionNormal, Heading3 } from "src/global/ui/typography/Text/Text.tsx"
import { useEventAction } from "src/global/hooks/useEventAction"
import { EventTypes } from "src/types/events"
import { useRealTimeUpdateMessage } from "src/global/hooks/useRealTimeUpdateMessage"
import { RequestSourceCard } from "src/pages/DataAssets/components/RequestSourceCard.tsx"
import { useIsFeatureEnabled } from "src/global/hooks/useIsFeatureEnabled.ts"
import { theme } from "src/global/ui/themes/theme.ts"

type Props = {
  onChoice: (choice: DataSourceChoice) => void
  createNew?: boolean
}

const REAL_TIME_BUFFER_DELAY = 1000
export const REAL_TIME_UPDATE_MESSAGE = "One of your Data Sources has changed"

export function PickNewOrExistingDataSource({ onChoice }: Props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rerender, setRerender] = useState<number>(Math.random())
  const { data, loading, refetch } = useQuery(DatasourcesWithRunsDocument, {
    onCompleted: (data) => {
      data.datasourcesV2.sort((a, b) => a.name.localeCompare(b.name))
      setRerender(Math.random()) // small, inexpensive hack to trigger a re-render without mutating meaningful state
    },
  })
  const { show: showDataSourceChangedMessage, container: dataSourceChangedMessage } = useRealTimeUpdateMessage({
    messageContent: REAL_TIME_UPDATE_MESSAGE,
    onRefresh: () => {
      refetch()
    },
  })
  const hasDataSources = Boolean(data?.datasourcesV2.length)
  const [createNew, setCreateNew] = useState(false)
  const isDataSourcesPaintedDoorEnabled = useIsFeatureEnabled("isDataSourcesPaintedDoorEnabled")

  /*
   * Handle real-time event updates
   */
  useEventAction(EventTypes.DATA_SOURCE.CREATED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })
  useEventAction(EventTypes.DATA_SOURCE.UPDATED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })
  useEventAction(EventTypes.DATA_SOURCE.DELETED, () => showDataSourceChangedMessage(), {
    bufferDelay: REAL_TIME_BUFFER_DELAY,
  })

  return (
    <Flex vertical gap="middle" style={{ padding: "24px" }}>
      {dataSourceChangedMessage}
      {hasDataSources && (
        <Row>
          <Col xs={20} sm={16} md={12} style={{ height: "30px" }}>
            <Segmented
              block={true}
              value={createNew ? "New Data Source" : "Existing Data Source"}
              options={["Existing Data Source", "New Data Source"]}
              onChange={(value) => (value === "New Data Source" ? setCreateNew(true) : setCreateNew(false))}
            />
          </Col>
        </Row>
      )}
      {createNew && <DataSourceCards setChoice={onChoice} isDSPaintedDoorEnabled={isDataSourcesPaintedDoorEnabled} />}
      {createNew && isDataSourcesPaintedDoorEnabled && (
        <>
          <Heading3 style={{ marginTop: theme.spacing.s }}>Don&apos;t see your Data Source?</Heading3>
          <Row gutter={16}>
            <Col span={8}>
              <RequestSourceCard />
            </Col>
          </Row>
        </>
      )}
      {!createNew && (
        <DataSourceList
          data={data}
          loading={loading}
          onSelect={(dataSource: DatasourceWithRunsFragment, dataSourceType: NativeCloudSupportedDataSource) =>
            onChoice({ createNew: false, dataSource, dataSourceType })
          }
        />
      )}
    </Flex>
  )
}

type ListProps = {
  data: Maybe<DatasourcesWithRunsQuery>
  loading: boolean
  onSelect: (dataSource: DatasourceWithRunsFragment, dataSourceType: NativeCloudSupportedDataSource) => void
}

function DataSourceList({ data, loading, onSelect }: ListProps) {
  return (
    <List
      split
      dataSource={data?.datasourcesV2 ?? ([] as DatasourceWithRunsFragment[])}
      loading={loading}
      rowKey={(datasource) => datasource.id}
      renderItem={(datasource) => (
        <ListItem
          datasource={datasource}
          disabled={!isSupportedDataSource(datasource.type)}
          {...(isSupportedDataSource(datasource.type) && {
            onSelect: () => onSelect(datasource, datasource.type as unknown as NativeCloudSupportedDataSource),
          })}
        />
      )}
    />
  )
}

interface ListItemProps {
  datasource: DatasourceWithRunsFragment
  disabled: boolean
  onSelect?: () => void
}

function ListItem({ datasource, onSelect }: ListItemProps) {
  const isEditable = useIsDataSourceEditable(datasource.type)
  return (
    <List.Item style={{ ...{ cursor: !isEditable ? "not-allowed" : "pointer" } }} onClick={onSelect}>
      <List.Item.Meta
        title={<BodyStrong {...(!isEditable ? { $color: "colorTextSecondary" } : {})}>{datasource.name}</BodyStrong>}
        avatar={
          <StyledLogoContainer>
            <img src={getImageLogo(datasource.type)} alt={`${datasource.type} logo`} />
          </StyledLogoContainer>
        }
        description={<DescriptionNormal>{datasource.assets.length} Assets</DescriptionNormal>}
      />
    </List.Item>
  )
}
